import { getElementByKey } from "../engines/renderingEngine/renderer";
import { ComponentType } from "../..";
import { NodeType, ObjectNodeType } from "../SComponents/scomponents.constants";
import { StudioNodeType } from "@certa/studio/api";
import type { BoxProps } from "../engines/coreComponents/types";
import { SDOComponentType } from "../types/componentType.types";

export const getFieldNodeKey = (nodeId: string) =>
  `${NodeType.FIELD}-${nodeId}`;

export const getStepNodeKey = (nodeId: string) => `${NodeType.STEP}-${nodeId}`;

export const getSwimlaneNodeKey = (nodeId: string) =>
  `${NodeType.SWIMLANE}-${nodeId}`;

export const getAddFieldNodeKey = (nodeId: string) =>
  `${NodeType.ADD_FIELD}-${nodeId}`;

export const getAddStepNodeKey = (nodeId: string) =>
  `${NodeType.ADD_STEP}-${nodeId}`;

export const getAddSwimlaneNodeKey = (nodeId: string) =>
  `${NodeType.ADD_SWIMLANE}-${nodeId}`;

export const getProcessNodeKey = (nodeId: string) =>
  `${NodeType.PROCESS}-${nodeId}`;

export const getProcessNodeFlowKey = (nodeId: string) =>
  `${NodeType.PROCESS_FLOW}-${nodeId}`;

export const getFlowSwimlaneKey = (nodeId: string) =>
  `${NodeType.FLOW_SWIMLANE}-${nodeId}`;

export const getFlowStepKey = (nodeId: string) =>
  `${NodeType.FLOW_STEP}-${nodeId}`;

export const getFlowSwimlaneColumnKey = (column: string) =>
  `${NodeType.FLOW_SWIMLANE_COLUMN}-${column}`;
export const getFlowSwimlaneRowKey = (row: string) =>
  `${NodeType.FLOW_SWIMLANE_ROW}-${row}`;

export const getFlowSwimlaneUpwardsRuleIndicatorKey = (nodeId: string) =>
  `${NodeType.FLOW_SWIMLANE_UPWARDS_RULE_INDICATOR}-${nodeId}`;

export const getFlowSwimlaneDownwardsRuleIndicatorKey = (nodeId: string) =>
  `${NodeType.FLOW_SWIMLANE_DOWNWARDS_RULE_INDICATOR}-${nodeId}`;

export const getFlowStepColumnKey = (swimlaneId: string, column: string) =>
  `${NodeType.FLOW_STEP_COLUMN}-${swimlaneId}-${column}`;

export const getSectionNodeKey = (nodeId: string) =>
  `${ObjectNodeType.SECTION}-${nodeId}`;

export const getSectionAddFieldNodeKey = (nodeId: string) =>
  `${ObjectNodeType.ADD_FIELD}-${nodeId}`;

export const getObjectRelationshipsNodeKey = () =>
  `${ObjectNodeType.OBJECT_RELATIONSHIPS_NODE}`;

export const getObjectRelationshipNodeKey = (nodeId: string) =>
  `${ObjectNodeType.OBJECT_RELATIONSHIP}-${nodeId}`;

export const getObjectNodeKey = (nodeId: string) =>
  `${ObjectNodeType.OBJECT}-${nodeId}`;

export const getObjectFieldKey = (nodeId: string) =>
  `${ObjectNodeType.FIELD}-${nodeId}`;

/**
 * This function converts a webGL node ID to a UUID or Nano ID.
 * For example:-
   Swimlane-4205__1d77f9bc-d9c2-499c-a63a-8ea5b9a63dac into 4205__1d77f9bc-d9c2-499c-a63a-8ea5b9a63dac.
 * @param nodeId
 * @returns string
 */
export const getComponentIdFromWebGLNodeId = (nodeId?: string) => {
  if (!nodeId) return "";
  return nodeId.slice(nodeId.indexOf("-") + 1);
};

export const getWebGLFieldNodeIdFromComponentId = (componentId: string) => {
  return getFieldNodeKey(componentId);
};

export const getWebGLStepNodeIdFromComponentId = (componentId: string) => {
  return getStepNodeKey(componentId);
};

export const getWebGLSwimlaneNodeIdFromComponentId = (componentId: string) => {
  return getSwimlaneNodeKey(componentId);
};

const getIsFlowSwimlaneUpwardsRuleIndicator = (nodeId: string) => {
  return nodeId.startsWith(NodeType.FLOW_SWIMLANE_UPWARDS_RULE_INDICATOR);
};

export const getSwimlaneIdFromFlowSwimlaneUpwardsRuleIndicatorKey = (
  nodeId: string
) => {
  return getIsFlowSwimlaneUpwardsRuleIndicator(nodeId)
    ? nodeId.slice(nodeId.indexOf("-") + 1)
    : undefined;
};

const getIsFlowSwimlaneDownwardsRuleIndicator = (nodeId: string) => {
  return nodeId.startsWith(NodeType.FLOW_SWIMLANE_DOWNWARDS_RULE_INDICATOR);
};

export const getSwimlaneIdFromFlowSwimlaneDownwardsRuleIndicatorKey = (
  nodeId: string
) => {
  return getIsFlowSwimlaneDownwardsRuleIndicator(nodeId)
    ? nodeId.slice(nodeId.indexOf("-") + 1)
    : undefined;
};

/**
 * Returns the component type of the given component instance.
 * @param componentInstance
 * @returns
 */
export const getComponentType = (
  componentKey: string
): ComponentType | null => {
  const componentInstance = getElementByKey(componentKey);
  if (!componentInstance) {
    return null;
  }

  const key = componentKey.split("-")[0];

  switch (key) {
    case NodeType.SWIMLANE:
    case NodeType.FLOW_SWIMLANE:
      return ComponentType.SWIMLANE;
    case NodeType.STEP:
    case NodeType.FLOW_STEP:
      return ComponentType.STEP;
    case NodeType.FIELD:
      return ComponentType.FIELD;
    default:
      return null;
  }
};

export const getComponentIdFromComponentKey = (componentKey: string) => {
  return componentKey.slice(componentKey.indexOf("-") + 1);
};

export const getSDOComponentType = (
  componentKey: string
): SDOComponentType | null => {
  const componentInstance = getElementByKey(componentKey);
  if (!componentInstance) {
    return null;
  }

  const key = componentKey.split("-")[0];

  switch (key) {
    case ObjectNodeType.OBJECT:
      return SDOComponentType.OBJECT;
    case ObjectNodeType.SECTION:
      return SDOComponentType.SECTION;
    case ObjectNodeType.FIELD:
      return SDOComponentType.FIELD;
    case ObjectNodeType.OBJECT_RELATIONSHIPS_NODE:
      return SDOComponentType.OBJECT_RELATIONSHIPS_NODE;
    case ObjectNodeType.OBJECT_RELATIONSHIP:
      return SDOComponentType.OBJECT_RELATIONSHIP;
    default:
      return null;
  }
};

export const getElementParentIds = (elementKey: string) => {
  const data: {
    [ComponentType.STEP]: string | undefined;
    [ComponentType.SWIMLANE]: string | undefined;
  } = {
    [ComponentType.STEP]: undefined,
    [ComponentType.SWIMLANE]: undefined
  };

  const type = getComponentType(elementKey);

  if (!type || type === ComponentType.SWIMLANE) return data;

  const element = getElementByKey(elementKey);

  if (!element) return data;

  if (type === ComponentType.STEP) {
    // Q: Why parentNode?.parentNode ?
    // A: In our WebGL implementation, The first parentNode is the "Body" node, the second parentNode is the actual parent node
    const stepProps = element.props as BoxProps;
    const swimlaneId = stepProps.variables?.swimlaneId;
    data[ComponentType.SWIMLANE] = swimlaneId;
  }

  if (type === ComponentType.FIELD) {
    const fieldProps = element.props as BoxProps;
    const stepId = fieldProps.variables?.stepId;
    const swimlaneId = fieldProps.variables?.swimlaneId;
    data[ComponentType.STEP] = stepId;
    data[ComponentType.SWIMLANE] = swimlaneId;
  }

  return data;
};

export const getWebGLNodeTypeToComponentType = (
  type: ComponentType | null
): StudioNodeType | undefined => {
  switch (type) {
    case ComponentType.SWIMLANE:
      return StudioNodeType.SWIMLANE;
    case ComponentType.STEP:
      return StudioNodeType.STEP;
    case ComponentType.FIELD:
      return StudioNodeType.FIELD;
    default:
      return undefined;
  }
};
